import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, defer, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthUser, AuthResponse } from '../models';
import { CognitoClient } from './cognito-client.service';

/** The signed-in person's details, as /api/auth/me returns them. */
type CurrentUser = Omit<AuthResponse, 'token'>;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'ef_token';   // the API's own token: the client portal only
  private readonly USER_KEY  = 'ef_user';
  private readonly KIND_KEY  = 'ef_auth';    // how this browser is signed in: 'cognito' or 'portal'

  private _user = new BehaviorSubject<AuthUser | null>(this.loadStoredUser());
  readonly currentUser$ = this._user.asObservable();

  constructor(private http: HttpClient, private router: Router, private cognito: CognitoClient) {}

  /**
   * Signs in through the specialist Cognito pool. 'newPasswordRequired' means this is an administrator-created
   * user's first sign-in: call {@link completeNewPassword} to finish.
   */
  signIn(email: string, password: string): Observable<'done' | 'newPasswordRequired'> {
    return defer(() => from(this.cognito.signIn(email, password))).pipe(
      switchMap(step => step === 'done'
        ? this.loadProfile().pipe(map(() => 'done' as const))
        : of('newPasswordRequired' as const)));
  }

  completeNewPassword(newPassword: string): Observable<void> {
    return defer(() => from(this.cognito.confirmNewPassword(newPassword))).pipe(switchMap(() => this.loadProfile()));
  }

  /** Emails a reset code. */
  forgotPassword(email: string): Observable<void> {
    return defer(() => from(this.cognito.resetPassword(email)));
  }

  resetPassword(email: string, code: string, newPassword: string): Observable<void> {
    return defer(() => from(this.cognito.confirmResetPassword(email, code, newPassword)));
  }

  logout(): void {
    this.clearSession();
    void this.cognito.signOut();   // also drops any pool tokens left behind by an earlier pool sign-in
    this.router.navigate(['/login']);
  }

  /** Whether this browser holds any sign-in at all, signed in properly or not. */
  hasSession(): boolean {
    return !!(this._user.value || localStorage.getItem(this.TOKEN_KEY) || localStorage.getItem(this.KIND_KEY));
  }

  /** The API's own token (client portal sessions only). */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /** The bearer token to send with an API request: the pool's ID token, or the client portal's token. */
  async bearerToken(): Promise<string | null> {
    return this.signedInWithCognito ? this.cognito.idToken() : this.getToken();
  }

  isAuthenticated(): boolean {
    if (this.signedInWithCognito) return !!this._user.value;   // Amplify refreshes the tokens; a dead session gets a 401
    const token = this.getToken();
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp > Date.now() / 1000;
    } catch {
      return false;
    }
  }

  get currentUser(): AuthUser | null {
    return this._user.value;
  }

  get role(): string | null {
    return this._user.value?.role ?? null;
  }

  get partnerId(): string | null {
    return this._user.value?.partnerId ?? null;
  }

  get clientId(): string | null {
    return this._user.value?.clientId ?? null;
  }

  get partnerStatus(): string | null {
    return this._user.value?.partnerStatus ?? null;
  }

  /** Update the cached partner onboarding status (e.g. after polling /partners/me). */
  setPartnerStatus(status: string | null): void {
    const u = this._user.value;
    if (!u) return;
    const updated = { ...u, partnerStatus: status };
    localStorage.setItem(this.USER_KEY, JSON.stringify(updated));
    this._user.next(updated);
  }

  navigateAfterLogin(): void {
    const role = this.role;
    if (role === 'Admin')        this.router.navigate(['/admin/dashboard']);
    else if (role === 'Partner') this.router.navigate([this.partnerStatus === 'Active' ? '/partner/dashboard' : '/partner/onboarding']);
    else if (role === 'Client')  this.router.navigate(['/client/dashboard']);
    else                         this.router.navigate(['/login']);
  }

  /** Establish a session from an auth response (used by the client portal token exchange). */
  setSession(res: AuthResponse): void {
    if (this.signedInWithCognito) void this.cognito.signOut();   // the portal session replaces a pool one
    localStorage.setItem(this.TOKEN_KEY, res.token);
    localStorage.setItem(this.KIND_KEY, 'portal');
    this.storeUser(res);
  }

  private get signedInWithCognito(): boolean {
    return localStorage.getItem(this.KIND_KEY) === 'cognito';
  }

  /** After the pool has signed the person in: ask the API who they are, which also proves it accepts their token. */
  private loadProfile(): Observable<void> {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.setItem(this.KIND_KEY, 'cognito');
    return this.http.get<CurrentUser>(`${environment.apiUrl}/auth/me`).pipe(
      tap(me => this.storeUser(me)),
      map(() => undefined),
      catchError(err => {
        // Signed in to the pool but not known to Energy Alliez (or the API is down): do not stay half signed in.
        this.clearSession();
        void this.cognito.signOut();
        return throwError(() => err);
      }));
  }

  private clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.KIND_KEY);
    this._user.next(null);
  }

  private storeUser(res: CurrentUser): void {
    const user: AuthUser = {
      email:         res.email,
      name:          res.name,
      role:          res.role as 'Admin' | 'Partner' | 'Client',
      partnerId:     res.partnerId,
      clientId:      res.clientId ?? null,
      company:       res.company,
      partnerStatus: res.partnerStatus ?? null,
    };
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this._user.next(user);
  }

  private loadStoredUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(this.USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
